import React, { useState, useEffect } from 'react';
import {
    Button,
    Box,
    Typography,
    Alert,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    CircularProgress,
    Divider
} from '@mui/material';
import BaseDialog from '../common/BaseDialog';
import { AppointmentTypeWithShares, appointmentTypeShareService } from '../../services/appointmentTypeShareService';
import { businessService } from '../../services/businessService';
import { BusinessUser } from '../../types/business';
import { useAuth } from '../../hooks/useAuth';
import { sortUserShares } from '../../utils/userHelpers';
import { extractApiErrorMessage } from '../../utils/apiUtils';

interface ManageSharesDialogProps {
    open: boolean;
    onClose: () => void;
    appointmentType: AppointmentTypeWithShares;
    onSuccess: () => void;
}

interface UserShareState {
    userId: string;
    fullName: string;
    email: string;
    isShared: boolean;
    originalIsShared: boolean;
    isAdmin: boolean;
}

const ManageSharesDialog: React.FC<ManageSharesDialogProps> = ({
    open,
    onClose,
    appointmentType,
    onSuccess
}) => {
    const auth = useAuth();
    const [userShares, setUserShares] = useState<UserShareState[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            loadUsersAndShares();
        }
    }, [open, appointmentType]);

    const loadUsersAndShares = async () => {
        setLoading(true);
        setError(null);
        try {
            // Get all business users
            const users = await businessService.getBusinessUsers();

            // Get current shares for this appointment type
            const shares = await appointmentTypeShareService.getShares(appointmentType.id);

            // Build the user share state, excluding the current user
            const shareMap = new Map(shares.map(s => [s.userId, s]));

            const userShareStates: UserShareState[] = sortUserShares(
                users
                    .filter(user => user.id !== auth?.user?.id) // Exclude current user
                    .map(user => {
                        const existingShare = shareMap.get(user.id);
                        return {
                            userId: user.id,
                            fullName: `${user.firstName} ${user.lastName}`.trim(),
                            email: user.email,
                            isShared: !!existingShare,
                            originalIsShared: !!existingShare,
                            isAdmin: user.businessRoles?.some(role => role.isBusinessAdmin) ?? false
                        };
                    })
            );

            setUserShares(userShareStates);
        } catch (err) {
            console.error('Error loading users and shares:', err);
            setError('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleShareToggle = (userId: string) => {
        setUserShares(prev => prev.map(user => {
            if (user.userId === userId) {
                return {
                    ...user,
                    isShared: !user.isShared
                };
            }
            return user;
        }));
    };

    const handleSelectAll = () => {
        const allSelected = userShares.every(u => u.isShared);
        setUserShares(prev => prev.map(user => ({
            ...user,
            isShared: !allSelected
        })));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);

        try {
            // Find changes
            const toAdd: string[] = [];
            const toRemove: string[] = [];

            for (const user of userShares) {
                if (user.isShared && !user.originalIsShared) {
                    // New share
                    toAdd.push(user.userId);
                } else if (!user.isShared && user.originalIsShared) {
                    // Removed share
                    toRemove.push(user.userId);
                }
            }

            // Get current shares to find share IDs for deletes
            const currentShares = await appointmentTypeShareService.getShares(appointmentType.id);
            const shareByUserId = new Map(currentShares.map(s => [s.userId, s]));

            // Process removals
            for (const userId of toRemove) {
                const share = shareByUserId.get(userId);
                if (share) {
                    await appointmentTypeShareService.deleteShare(appointmentType.id, share.id);
                }
            }

            // Process additions - need to get user emails
            const users = await businessService.getBusinessUsers();
            const userEmailMap = new Map(users.map(u => [u.id, u.email]));

            for (const userId of toAdd) {
                const email = userEmailMap.get(userId);
                if (email) {
                    await appointmentTypeShareService.createShare(appointmentType.id, {
                        userEmail: email,
                        canWrite: true // Always grant full write access
                    });
                }
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            console.error('Error saving shares:', err);
            setError(extractApiErrorMessage(err, 'Failed to save changes'));
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = userShares.some(u => u.isShared !== u.originalIsShared);

    const selectedCount = userShares.filter(u => u.isShared).length;
    const allSelected = userShares.length > 0 && userShares.every(u => u.isShared);

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            title={`Manage Sharing - ${appointmentType.name}`}
            maxWidth="sm"
            actions={
                <>
                    <Button onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={saving || !hasChanges}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </>
            }
        >
            {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : userShares.length === 0 ? (
                <Box py={4} textAlign="center">
                    <Typography color="text.secondary">
                        No other users in your business to share with.
                    </Typography>
                </Box>
            ) : (
                <>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {selectedCount} of {userShares.length} users selected
                        </Typography>
                        <Button size="small" onClick={handleSelectAll}>
                            {allSelected ? 'Deselect All' : 'Select All'}
                        </Button>
                    </Box>

                    <Divider />

                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {userShares.map((user, index) => (
                            <ListItem
                                key={user.userId}
                                divider={index < userShares.length - 1}
                                sx={{ py: 1, cursor: 'pointer' }}
                                onClick={() => handleShareToggle(user.userId)}
                            >
                                <Checkbox
                                    checked={user.isShared}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={() => handleShareToggle(user.userId)}
                                    sx={{ mr: 1 }}
                                />
                                <ListItemText
                                    primary={user.fullName}
                                    secondary={user.email}
                                />
                            </ListItem>
                        ))}
                    </List>
                </>
            )}
        </BaseDialog>
    );
};

export default ManageSharesDialog;
